import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import TroubleshootDetailsModal from './TroubleshootDetailsModal'
import Breadcrumb from '../common/Breadcrumb'
import LoadingSpinner from '../common/LoadingSpinner'
import DataPlaceholder from '../common/DataPlaceholder'
import DashboardHeader from '../common/DashboardHeader/DashboardHeader'
import EmptyPageResults from '../common/EmptyPageResults'
import VideoModal from '../common/VideoModal'
import { toast } from '../common/Toast'
import Dropdown from '../common/Dropdown'
import { useWindowSize } from '../../libs/hooks'
import { getImageByModel } from '../../utils/functions'
import { getModelsTrobleshoot } from '../../utils/requests/troubleshootingAPI'
import { hasEndUserSiteAccess } from '../../utils/permissionValidation'
import { trackEvent, gaActions, gaCategories } from '../../utils/ga4'
import './troubleshoot.scss'

const Troubleshoot = (props) => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const hideBreadcrumb = props.hideBreadcrumb
  const stateModel = location.state && location.state.model
  const stateCycleFault = location.state && location.state.cycleFault
  const canGoBack = location.state && location.state.withBackNavigation
  const isMobile = useWindowSize()[0] <= 768
  const [products, setProducts] = useState(null)
  const [cycles, setCycles] = useState(null)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [selectedCycle, setSelectedCycle] = useState(null)
  const [videos, setVideos] = useState([])
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [showSpinner, setShowSpinner] = useState(null)
  const [isTroubleshootModal, setIsTroubleshootModal] = useState(props.isTroubleshootModal)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const model = props.model || stateModel
  const isEndUser = hasEndUserSiteAccess(props.userPermissions)

  useEffect(() => {
    if (!props.isTroubleshootModal) {
      trackEvent({
        action: gaActions.featureClick,
        category: gaCategories.troubleshootingPage,
        label: 'Troubleshooting Page: Page View',
      })
    }
  }, [])

  useEffect(() => {
    if (isEndUser && Object.keys(props.userProducts || {}).length > 0) {
      setShowSpinner(true)

      const groupedProducts = [...new Set(Object.values(props.userProducts).map((product) => product.model))]

      const sortedModels = groupedProducts
        .filter((model) => model.length > 0)
        .filter(
          (model) =>
            !model.toLowerCase().includes('biosonic') && !model.toLowerCase().includes('canalpro jeni evo') && !model.toLowerCase().includes('canalpro jeni'),
        )
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))

      setProducts(sortedModels)
      setShowSpinner(false)
    }
  }, [props.userProducts])

  useEffect(() => {
    if (isFirstLoad && !props.userProductsAreLoading) {
      setIsFirstLoad(false)
    }
  }, [props.userProductsAreLoading])

  useEffect(() => {
    if (!isEndUser && Object.keys(props.productTypes || {}).length > 0) {
      toggleSpinner(true)
      mapProductTypesToProducts(props.productTypes)
      toggleSpinner(false)
    }

    if (model && !selectedProduct) {
      setSelectedProduct(model)
      getProductData(model)
      setIsTroubleshootModal(props.model)
    }
  }, [props.productTypes])

  useEffect(() => {
    if (selectedCycle) {
      const state = location.state
      delete state?.model
      delete state?.cycleFault
      history.replace(`${history.location.pathname}`, { ...state })
    }
  }, [selectedCycle])

  const mapProductTypesToProducts = (productTypes) => {
    let arr = []
    for (const index in productTypes) {
      const deviceModels = productTypes[index].models
      deviceModels.map((model) => {
        arr.push(model.name)
      })
    }
    const filtered = arr.filter(
      (model) =>
        !model.toLowerCase().includes('biosonic') && !model.toLowerCase().includes('canalpro jeni evo') && !model.toLowerCase().includes('canalpro jeni'),
    )
    setProducts(filtered.sort())
    setIsFirstLoad(false)
  }

  const getProductData = async (model) => {
    if (model) {
      toggleSpinner(true)

      try {
        const modelsTroubleshootParams = {
          model: model,
        }

        //TODO: Add back section once videos for troubelshooting are ready
        // const videosQueryParams = {
        //   category: "Troubleshooting",
        //   page: 1,
        //   model: model,
        //   limit: 3
        // }

        // const [cyclesData, videosData] = await Promise.all([getModelsTrobleshoot(modelsTroubleshootParams), getVideosList(videosQueryParams)])
        const [cyclesData] = await Promise.all([getModelsTrobleshoot(modelsTroubleshootParams)])

        if (cyclesData.data) {
          let arr = []

          cyclesData.data.map((item) => {
            arr.push(item.faults)
          })

          const result = [].concat(...arr)
          setCycles(result)

          if (stateCycleFault) {
            const preselectedCycleFault = result.find((cycle) => cycle.fault === stateCycleFault)

            if (preselectedCycleFault) {
              setSelectedCycle(preselectedCycleFault)
            }
          }
        }

        // if (videosData.data) {
        //   setVideos(videosData.data)
        // }

        // if (!cyclesData.data && !videosData.data) {
        //   toast.error(t("error.failure_msg"))
        //   setSelectedProduct(null)
        // }

        if (!cyclesData.data) {
          toast.error(t('error.failure_msg'))
          setSelectedProduct(null)
        }
      } catch (error) {
        toast.error(t('error.failure_msg'))
        setSelectedProduct(null)
      } finally {
        toggleSpinner(false)
      }
    }
  }

  const selectProduct = (product) => {
    setSelectedProduct(product)

    setCycles(null)
    setSelectedCycle(null)
    getProductData(product)
  }

  const selectCycle = (cycleId) => {
    const cycle = cycles.find((c) => c.id === cycleId)
    
    if (cycle) {
      trackEvent({
        action: gaActions.featureClick,
        category: gaCategories.troubleshootingPage,
        label: `Troubleshooting Page: Cycle Selected - ${cycle.fault}`,
      })
    }
    
    setSelectedCycle(cycle)
  }

  const toggleSpinner = (val) => {
    setShowSpinner(val)
  }

  const openVideoModal = (item) => {
    setSelectedVideo(item)
  }

  const closeVideoModal = () => {
    setSelectedVideo(null)
  }

  const handleNavigateToVideos = () => {
    history.push({
      pathname: `/videos`,
      state: {
        model: selectedProduct,
      },
    })
  }

  return (
    <div className="troubleshoot-wrapper d-flex flex-column">
      {!hideBreadcrumb && <Breadcrumb path={window.location.pathname} withBackNavigation={canGoBack} />}
      <DashboardHeader headerText={t('nav.troubleshoot')} />
      <div className="separator" />
      {!isTroubleshootModal && (
        <>
          {!isMobile ? (
            <div className="content">
              {products?.length > 0 ? (
                <>
                  <div className="products">
                    {products.map((item, index) => (
                      <div
                        key={index}
                        className={'card d-flex flex-align-center' + (selectedProduct === item ? ' selected-card' : '')}
                        onClick={() => selectProduct(item)}
                      >
                        <div className="image-wrapper d-flex flex-align-center">
                          <img src={getImageByModel(item)} className="img" alt={item} />
                        </div>
                        <p className="product-name">{item}</p>
                      </div>
                    ))}
                  </div>

                  {selectedProduct ? (
                    <div className="cycles-wrapper">
                      <p className="cycle-select-title">{t('troubleshoot.select-option')}</p>
                      <Dropdown
                        className="dropdown-input"
                        placeholder={t('troubleshoot.no-option')}
                        options={cycles?.map((cycle) => ({ key: cycle.id, value: cycle.fault }))}
                        initialSelectedKey={selectedCycle?.id}
                        onChange={(cycle) => selectCycle(cycle.key)}
                        withFiltering
                      />

                      {selectedCycle && (
                        <div className="cycle-details-wrapper">
                          <div className="cycle-title-wrapper">
                            <div className="cycle-title">
                              <span>{t('fault')}</span>
                              <span className="cycle-title-number">{selectedCycle.fault}</span>
                            </div>
                            {selectedCycle.description?.length > 0 && (
                              <div className="cycle-category d-flex flex-column">
                                <ul className="list-none">
                                  {selectedCycle.description.map((el, index) => (
                                    <li key={index} className="cycle-categories">
                                      <ul className="cycle-category-issue-list">
                                        {el.split('\\n').map((desc, subIndex) => (
                                          <li key={subIndex} className="cycle-description">
                                            {desc}
                                          </li>
                                        ))}
                                      </ul>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                          {selectedCycle.probable_cause?.length > 0 && (
                            <div className="cycle-category d-flex flex-column">
                              <h3 className="cycle-category-title d-flex flex-align-center">{t('troubleshoot.probable-cause')}</h3>
                              <ul className="list-none">
                                {selectedCycle.probable_cause.map((el, index) => (
                                  <li key={index} className="cycle-categories">
                                    <ul className="cycle-category-issue-list">
                                      {el.split('\\n').map((el, index) => (
                                        <li key={index} className="cycle-category-issue">
                                          {el}
                                        </li>
                                      ))}
                                    </ul>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {!isEndUser && selectedCycle.tech_troubleshooting?.length > 0 && (
                            <div className="cycle-category d-flex flex-column">
                              <h3 className="cycle-category-title d-flex flex-align-center">{t('troubleshoot.tech')}</h3>
                              <ul className="list-none">
                                {selectedCycle.tech_troubleshooting.map((el, index) => (
                                  <li key={index} className="cycle-categories">
                                    <ul className="cycle-category-issue-list">
                                      {el.split('\\n').map((el, index) => (
                                        <li key={index} className="cycle-category-issue">
                                          {el}
                                        </li>
                                      ))}
                                    </ul>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {selectedCycle.user_troubleshooting?.length > 0 && (
                            <div className="cycle-category d-flex flex-column">
                              <h3 className="cycle-category-title d-flex flex-align-center">{t('troubleshoot.user-troubleshoot')}</h3>
                              <ul className="list-none">
                                {selectedCycle.user_troubleshooting.map((el, index) => (
                                  <li key={index} className="cycle-categories">
                                    <ul className="cycle-category-issue-list">
                                      {el.split('\\n').map((el, index) => (
                                        <li key={index} className="cycle-category-issue">
                                          {el}
                                        </li>
                                      ))}
                                    </ul>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {/*TODO: Add back section once videos for troubelshooting are ready */}
                          {/* <div className="product-videos-wrapper">
                            <div className="section-title">{t("nav.videos")}</div>
                            <div className="videos-wrapper">
                              {videos.map((video, index) => (
                                <div
                                  key={index}
                                  className="video-item-wrapper"
                                  onClick = {() => openVideoModal(video)}
                                >
                                  <div className="thumbnail-wrapper">
                                    <div className="thumbnail-icon">
                                      <VideoPlayIcon />
                                    </div>
                                    <img
                                      src={video.thumbnail_url}
                                      alt="video"
                                    />
                                  </div>                
                                  <p className="title">{video.title}</p>
                                </div>))
                              }
                              {videos.length === 0 && 
                                <div className="no-videos-wrapper">
                                  <p className="disclaimer">{t("video_context.no_videos_yet")}</p>
                                </div>
                              }
                            </div>
                            <SimpleButton className="action-button" onClick={() => handleNavigateToVideos()}>
                              {t("video_context.view_all_videos")}
                            </SimpleButton>
                          </div> */}
                        </div>
                      )}
                      {!selectedCycle && <EmptyPageResults title={t('troubleshoot.no-fault-selected')} subtitle={t('troubleshoot.select-fault')} />}
                    </div>
                  ) : (
                    <DataPlaceholder titlePlaceholder={t('no_product_selected')} subtitlePlaceholder={t('troubleshoot.select-product')} />
                  )}
                </>
              ) : (
                <>
                  {!isFirstLoad && (
                    <EmptyPageResults className="empty-page" title={t('device_context.product_not_found')} subtitle={t('troubleshoot.no-products')} />
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="content">
              {products?.map((item, index) => (
                <div key={index} className="card d-flex flex-align-center" onClick={() => selectProduct(item)}>
                  <div className="image-wrapper d-flex flex-align-center">
                    <img src={getImageByModel(item)} className="img" alt={item} />
                  </div>
                  <p className="product-name">{item}</p>
                </div>
              ))}
              {!isFirstLoad && !products?.length > 0 && (
                <EmptyPageResults className="empty-page" title={t('device_context.product_not_found')} subtitle={t('troubleshoot.no-products')} />
              )}
            </div>
          )}
        </>
      )}
      {isTroubleshootModal && (
        <div className="troubleshoot-modal-wrapper">
          <div className="products-wrapper">
            <p className="product-select-title">{t('troubleshoot.select-product')}</p>
            <Dropdown
              className="dropdown-input"
              placeholder={t('no_product_selected')}
              options={products?.map((product) => ({ key: product, value: product }))}
              initialSelectedKey={selectedProduct}
              onChange={(product) => selectProduct(product.key)}
              withFiltering
            />
          </div>
          <div className={'cycles-wrapper' + (!selectedProduct ? ' disabled' : '')}>
            <p className="cycle-select-title">{t('troubleshoot.select-option')}</p>
            <Dropdown
              className="dropdown-input"
              placeholder={t('troubleshoot.no-option')}
              options={cycles?.map((cycle) => ({ key: cycle.id, value: cycle.fault }))}
              initialSelectedKey={selectedCycle?.id}
              onChange={(cycle) => selectCycle(cycle.key)}
              withFiltering
            />

            {selectedCycle && (
              <div className="cycle-details-wrapper">
                <div className="cycle-title">
                  <span>{t('fault')}</span>
                  <span className="cycle-title-number">{selectedCycle.fault}</span>
                </div>
                {selectedCycle.description?.length > 0 && (
                  <div className="cycle-category d-flex flex-column">
                    <ul className="list-none">
                      {selectedCycle.description.map((el, index) => (
                        <li key={index} className="cycle-categories">
                          <ul className="cycle-category-issue-list">
                            {el.split('\\n').map((desc, subIndex) => (
                              <li key={subIndex} className="cycle-description">
                                {desc}
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {selectedCycle.probable_cause?.length > 0 && (
                  <div className="cycle-category d-flex flex-column">
                    <h3 className="cycle-category-title d-flex flex-align-center">{t('troubleshoot.probable-cause')}</h3>
                    <ul className="cycle-category-issue-list">
                      {selectedCycle.probable_cause.map((el, index) => (
                        <li key={index} className="cycle-category-issue">
                          {el}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {selectedCycle.tech_troubleshooting?.length > 0 && (
                  <div className="cycle-category d-flex flex-column">
                    <h3 className="cycle-category-title d-flex flex-align-center">{t('troubleshoot.tech')}</h3>
                    <ul className="cycle-category-issue-list">
                      {selectedCycle.tech_troubleshooting.map((el, index) => (
                        <li key={index} className="cycle-category-issue">
                          {el}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {selectedCycle.user_troubleshooting?.length > 0 && (
                  <div className="cycle-category d-flex flex-column">
                    <h3 className="cycle-category-title d-flex flex-align-center">{t('troubleshoot.user-troubleshoot')}</h3>
                    <ul className="cycle-category-issue-list">
                      {selectedCycle.user_troubleshooting.map((el, index) => (
                        <li key={index} className="cycle-category-issue">
                          {el}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {isMobile && selectedProduct && !isTroubleshootModal && (
        <TroubleshootDetailsModal
          selectedProduct={selectedProduct}
          cycles={cycles}
          selectedCycle={selectedCycle}
          onSelectCycle={(cycle) => selectCycle(cycle)}
          videos={videos}
          selectedVideo={selectedVideo}
          openVideoModal={(video) => openVideoModal(video)}
          closeVideoModal={() => closeVideoModal()}
          showSpinner={showSpinner}
          closeModal={() => setSelectedProduct(null)}
          isEndUser={isEndUser}
        />
      )}
      {selectedVideo && <VideoModal title={selectedVideo.title} videoId={selectedVideo.video_id} onCloseVideo={closeVideoModal} />}
      {props.userProductsAreLoading ||
        (showSpinner && (
          <div className="spinner-wrapper">
            <LoadingSpinner />
          </div>
        ))}
    </div>
  )
}

Troubleshoot.propTypes = {
  model: PropTypes.string,
}

function mapStateToProps({ productTypes, userProducts, userPermissions }) {
  return {
    productTypes,
    userProducts: userProducts?.products || {},
    userProductsAreLoading: userProducts?.loading,
    userPermissions,
  }
}

export default connect(mapStateToProps)(Troubleshoot)

